import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { ChevronRight, Phone, ArrowRight, Info, Search } from "lucide-react";
import { PHONE, PHONE_HREF } from "@/data/services";

/**
 * Pricing Guide — rough price bands for common jobs.
 * Numbers are indicative only — final price always on the quote.
 */

const CATEGORIES = [
  {
    name: "Taps & Tapware",
    items: [
      { title: "Washer replacement — single tap", band: "$140 – $220", note: "In-stock washers · includes callout" },
      { title: "Full mixer tap replacement — supplied", band: "$350 – $650", note: "Depends on tap chosen" },
      { title: "Outside garden tap replacement", band: "$220 – $380", note: "Standard brass bibcock" },
      { title: "Shower mixer rebuild", band: "$290 – $520", note: "Cartridge, O-rings, test" },
    ],
  },
  {
    name: "Toilets",
    items: [
      { title: "Running cistern repair", band: "$180 – $320", note: "Inlet valve or flush valve" },
      { title: "Pan-to-floor reseal", band: "$180 – $280", note: "Includes re-set & silicone" },
      { title: "Full toilet replacement — supplied", band: "$780 – $1,400", note: "Depends on toilet type" },
      { title: "In-wall cistern repair", band: "$420 – $780", note: "Access panel + mechanism" },
    ],
  },
  {
    name: "Hot Water",
    items: [
      { title: "Element replacement — electric", band: "$320 – $480", note: "Includes drain-down" },
      { title: "Thermostat & tempering valve", band: "$280 – $450", note: "Compliance upgrade" },
      { title: "Gas storage replacement — supplied + installed", band: "$2,400 – $3,800", note: "Common sizes · same day where in stock" },
      { title: "Continuous flow gas install", band: "$2,800 – $4,200", note: "Wall-mounted · flue + gas run" },
      { title: "Heat pump replacement — supplied + installed", band: "$3,900 – $5,800", note: "Before rebates · see Rebate Finder" },
    ],
  },
  {
    name: "Blocked Drains",
    items: [
      { title: "Machine clear — single accessible blockage", band: "$280 – $420", note: "Fixed price · same-day where possible" },
      { title: "CCTV camera inspection", band: "$180 – $320", note: "Includes recording to your phone" },
      { title: "Hydro-jet clear", band: "$420 – $720", note: "For roots, grease, silt" },
      { title: "Pipe relining — per metre", band: "POA", note: "Scope varies — camera first" },
    ],
  },
  {
    name: "Emergency",
    items: [
      { title: "After-hours callout fee", band: "$180 – $280", note: "Then standard emergency rates" },
      { title: "Burst pipe make-safe", band: "$320 – $650", note: "Isolate + temporary repair" },
      { title: "Gas leak make-safe", band: "$350 – $600", note: "Isolation + certified repair" },
    ],
  },
  {
    name: "Civil & Drainage",
    items: [
      { title: "Site visit + quote for civil scope", band: "FREE", note: "On most civil enquiries" },
      { title: "Stormwater connection", band: "POA", note: "Depends on run length, trench depth, council" },
      { title: "Sewer branch line / new connection", band: "POA", note: "Fully documented, council-approved" },
    ],
  },
  {
    name: "Bathrooms",
    items: [
      { title: "Bathroom renovation — standard", band: "$22,000 – $38,000", note: "Full strip-out to handover" },
      { title: "Ensuite — compact", band: "$16,000 – $26,000", note: "Includes tiling & tapware" },
      { title: "Powder room refresh", band: "$8,000 – $14,000", note: "Vanity, toilet, tapware, tiling" },
    ],
  },
];

const PricingGuide = () => {
  const [q, setQ] = useState("");

  const normalised = q.trim().toLowerCase();
  const filtered = normalised
    ? CATEGORIES.map((c) => ({
        ...c,
        items: c.items.filter((it) =>
          it.title.toLowerCase().includes(normalised) || c.name.toLowerCase().includes(normalised)
        ),
      })).filter((c) => c.items.length > 0)
    : CATEGORIES;

  return (
    <div data-testid="pricing-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gold-glow opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-page px-6 lg:px-14 pt-20 pb-16">
          <nav className="text-xs tracking-widerx uppercase flex items-center gap-2 mb-12" aria-label="Breadcrumb">
            <Link to="/" className="text-muted hover:text-gold">Home</Link>
            <ChevronRight size={12} className="text-dim" />
            <span className="text-ink">Pricing Guide</span>
          </nav>
          <div className="max-w-3xl">
            <div className="eyebrow mb-6"><span>Transparent · Honest · Indicative</span></div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="font-display font-black text-[clamp(3rem,9vw,7.5rem)] leading-[0.9] text-ink"
            >
              Rough prices.
              <br />
              <span className="text-gold">No fine print.</span>
            </motion.h1>
            <p className="mt-8 text-muted text-lg md:text-xl max-w-xl leading-relaxed">
              Most plumbing sites hide this. Here's where our pricing usually lands — before
              callout fees, before fine print. The real number lives on your quote.
            </p>
          </div>
        </div>
      </section>

      <section className="border-y border-line bg-elevated sticky top-20 z-30">
        <div className="mx-auto max-w-page px-6 lg:px-14 py-5 flex items-center gap-3">
          <Search size={18} className="text-gold flex-shrink-0" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a job — e.g. 'burst pipe', 'tap', 'heat pump'"
            data-testid="pricing-search"
            className="flex-1 bg-transparent text-ink placeholder:text-muted py-2 outline-none"
          />
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-page px-6 lg:px-14 space-y-16">
          {filtered.map((cat) => (
            <div key={cat.name}>
              <div className="flex items-end justify-between mb-8 border-b border-line pb-4">
                <h2 className="font-display text-4xl md:text-5xl text-ink">{cat.name}</h2>
                <span className="text-muted text-xs tracking-widerx uppercase">{cat.items.length} item{cat.items.length !== 1 ? "s" : ""}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line">
                {cat.items.map((it, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.4, delay: i * 0.04 }}
                    data-testid={`pricing-item-${cat.name.toLowerCase().replace(/\s+/g, "-")}-${i}`}
                    className="bg-bg hover:bg-card transition-colors p-6 md:p-8 flex items-start justify-between gap-6"
                  >
                    <div>
                      <div className="font-display text-lg md:text-xl text-ink mb-2 leading-snug">{it.title}</div>
                      <div className="text-muted text-sm">{it.note}</div>
                    </div>
                    <div className="font-body font-medium text-gold text-xl md:text-2xl tabular-nums whitespace-nowrap flex-shrink-0">
                      {it.band}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-24 text-muted">No prices match "{q}". Try another term, or call us.</div>
          )}
        </div>
      </section>

      <section className="py-10">
        <div className="mx-auto max-w-page px-6 lg:px-14">
          <div className="p-6 border-l-2 border-gold/40 bg-gold/[0.03] flex items-start gap-3 max-w-3xl">
            <Info size={18} className="text-gold mt-0.5 flex-shrink-0" />
            <p className="text-muted text-sm leading-relaxed">
              Pricing varies with access, parts availability, scope on the day, and after-hours timing.
              These bands exist so you&apos;re not quoting blind — the real figure is confirmed on the quote
              before any work starts. We never bill above an agreed quote without written consent.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-elevated border-t border-line">
        <div className="mx-auto max-w-page px-6 lg:px-14 text-center">
          <h2 className="font-display text-5xl md:text-7xl text-ink mb-6">
            Want a real number?
          </h2>
          <p className="text-muted text-lg max-w-xl mx-auto mb-10">
            Describe your job and we'll quote within 24 hours. Emergency? Call us right now.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href={PHONE_HREF} className="btn-gold" data-testid="pricing-cta-call">
              <Phone size={16} /> Call {PHONE}
            </a>
            <Link to="/contact" className="btn-ghost" data-testid="pricing-cta-quote">
              Request a Quote <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingGuide;
